import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, Divider, Stack } from '@mui/material';
import { contactApi } from '../api/contact';
import { aboutApi } from '../api/about';
import PageLayout from '../components/layout/PageLayout';
import countryList from '../data/countries.json';
import BasicButton from '../components/button/BasicButton';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomTextField from '../components/common/CustomTextField';
import CustomTextareaAutosize from '../components/common/CustomTextareaAutosize';
import theme from '../theme';
import CustomSelectBox from '../components/common/CustomSelectBox';
import alertCompletedIcon from '../assets/alert_complete.svg';
import SEO from '../components/common/SEO';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../utils/env';
import { useUserStore } from '../stores/userStore';
import EditButton from '../components/button/EditButton';
import ReactQuillEditor from '../components/Editor/ReactQuillEditor';
import ContentViewer from '../components/common/ContentViewer';
import CancelConfirmDialog from '../components/dialog/CancelConfirmDialog';
import ProgressBar from '../components/common/ProgressBar';

const schema = yup.object().shape({
  name: yup.string().required('성함을 입력해 주세요.'),
  email: yup
    .string()
    .email('올바른 이메일 형식을 입력해 주세요.')
    .required('이메일을 입력해 주세요.')
    .matches(/@[^.]*\./, '올바른 이메일 형식을 입력해 주세요.'),
  telephone: yup.string(),
  location: yup.string(),
  message: yup.string().required('내용을 입력해 주세요.'),
});

const DEFAULT_CONTACT_DESC =
  '<p>협업, 광고 등의 문의는 아래 양식을 통해 보내주시면 검토 후 신속하게 회신드리겠습니다.</p><p>또한 서비스 이용 중 발생한 버그, 신고 및 건의사항도 아래 양식을 통해 접수해주세요.</p>';

const Contact = () => {
  const isAdmin = useUserStore((state) => state.isAdmin);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      telephone: '',
      location: '',
      message: '',
    },
  });
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [descContent, setDescContent] = useState('');
  const [descLoading, setDescLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  useEffect(() => {
    aboutApi
      .getContent('CONTACT')
      .then(({ data }) => setDescContent(data?.content || DEFAULT_CONTACT_DESC))
      .finally(() => setDescLoading(false));
  }, []);

  const onClickEdit = () => {
    setEditContent(descContent);
    setIsEditing(true);
  };

  const handleDescSubmit = async () => {
    try {
      const { data } = await aboutApi.updateContent(editContent, 'CONTACT');
      setDescContent(data.content);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating contact description:', error);
    }
  };

  const handleCancelConfirm = () => {
    setIsCancelDialogOpen(false);
    setIsEditing(false);
  };

  const onSubmit = async (formData) => {
    if (success) return;
    setSubmitError(false);
    try {
      await contactApi.sendEmail(formData);
      setSuccess(true);
      reset();
      window.scrollTo(0, 0);
    } catch {
      setSubmitError(true);
    }
  };

  if (isEditing) {
    return (
      <PageLayout>
        <Box sx={{ maxWidth: '780px', minWidth: '320px', mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              mb: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 'bold', fontSize: { lg: '28px', md: '22px', xs: '20px' } }}
            >
              문의
            </Typography>
          </Box>
          <ReactQuillEditor value={editContent} onChange={(value) => setEditContent(value)} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: { xs: '30px', lg: '50px' },
            }}
          >
            <BasicButton
              variant="outlined"
              onClick={() => setIsCancelDialogOpen(true)}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              취소
            </BasicButton>
            <BasicButton
              variant="contained"
              color="primary"
              onClick={handleDescSubmit}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              등록
            </BasicButton>
          </Box>
        </Box>
        <CancelConfirmDialog
          open={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          onConfirm={handleCancelConfirm}
        />
      </PageLayout>
    );
  }

  if (success) {
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems={'center'}>
          <img
            alt="alertCompletedIcon"
            src={alertCompletedIcon}
            sx={{ width: '32px', height: '32px' }}
          />
          <Typography fontSize={'20px'} fontWeight={'bold'}>
            제출이 완료되었습니다!
          </Typography>
        </Stack>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO
        title="문의"
        description="협업, 광고 등의 문의는 아래 양식을 통해 보내주시면 검토 후 신속하게 회신드리겠습니다."
        url={`${SITE_URL}/contact`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: '문의' },
            ],
          })}
        </script>
      </Helmet>
      <Box sx={{ maxWidth: '780px', minWidth: '320px', mx: 'auto' }}>
        <Stack>
          {/* 타이틀 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography
              sx={{ fontWeight: 'bold', fontSize: { lg: '28px', md: '22px', xs: '20px' } }}
            >
              문의
            </Typography>
            {isAdmin && <EditButton onToggle={onClickEdit} />}
          </Box>
          <Divider sx={{ width: '100%', marginTop: 2 }} />

          {/* 설명 */}
          <ProgressBar isLoading={descLoading}>
            <Box sx={{ width: '100%' }} mt={{ lg: 4, md: 2, xs: 1 }} mb={4}>
              <ContentViewer text={descContent} sx={{ color: theme.palette.black[4] }} />
            </Box>
          </ProgressBar>

          {/* 입력폼 */}
          <Grid container>
            <Grid item container spacing={'20px'} mb={'20px'}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="body3" color={theme.palette.black[7]}>
                    성함<span style={{ color: theme.palette.secondary.main }}>*</span>
                  </Typography>
                  <CustomTextField
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder="성함을 입력해 주세요."
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="body3" color={theme.palette.black[7]}>
                    이메일<span style={{ color: theme.palette.secondary.main }}>*</span>
                  </Typography>
                  <CustomTextField
                    {...register('email')}
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    placeholder="이메일을 입력해 주세요."
                    fullWidth
                  />
                </Stack>
              </Grid>
            </Grid>

            <Grid item container spacing={'20px'} mb={'20px'}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="body3" color={theme.palette.black[7]}>
                    연락처
                  </Typography>
                  <CustomTextField {...register('telephone')} fullWidth />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="body3" color={theme.palette.black[7]}>
                    국가
                  </Typography>
                  <CustomSelectBox
                    options={countryList}
                    {...register('location')}
                    value={watch('location')}
                    fullWidth
                  />
                </Stack>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <Typography variant="body3" color={theme.palette.black[7]}>
                  내용<span style={{ color: theme.palette.secondary.main }}>*</span>
                </Typography>
                <CustomTextareaAutosize
                  minRows={6.5}
                  {...register('message')}
                  error={!!errors.message}
                  helperText={errors.message?.message}
                  placeholder="내용을 입력해 주세요."
                  maxLength={5000}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>

        {submitError && (
          <Typography
            role="alert"
            sx={{
              mt: 2,
              color: (activeTheme) =>
                activeTheme.palette.mode === 'dark'
                  ? activeTheme.palette.error.light
                  : activeTheme.palette.error.main,
            }}
          >
            문의를 전송하지 못했습니다. 입력한 내용은 유지됩니다. 잠시 후 다시 시도해 주세요.
          </Typography>
        )}
        {/* 제출 버튼 */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Box
            display="flex"
            justifyContent="center"
            mt={{ lg: '50px', md: '40px', xs: '40px' }}
            sx={{ width: { xs: '100%', md: '19%', lg: '19%' } }}
          >
            <BasicButton
              fullWidth
              type="submit"
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              sx={{ height: '48px', minWidth: '150px' }}
            >
              제출
            </BasicButton>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default Contact;
